import clsx from 'clsx';
import { Check, Copy, Save, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ActionLabel, SaveActionLabelPayload } from '../actionLabels';
import { reviseText } from '../llmClient';
import type { LlmStatus, LlmTask } from '../types';

interface ActionLabelModalProps {
  startSecond: number;
  endSecond: number;
  episodeId: string;
  operator: string;
  // LLM state
  llmStatus: LlmStatus;
  candidates: LlmTask[];
  llmError: string | null;
  model: string | null;
  llmDurationS: number | null;
  // Existing saved label (for edit mode)
  existingLabel: ActionLabel | null;
  // Callbacks
  onSave: (startSecond: number, payload: SaveActionLabelPayload) => Promise<void>;
  onDelete: (startSecond: number) => Promise<void>;
  onClose: () => void;
}

export function ActionLabelModal({
  startSecond,
  endSecond,
  episodeId,
  operator,
  llmStatus,
  candidates,
  llmError,
  model,
  llmDurationS,
  existingLabel,
  onSave,
  onDelete,
  onClose,
}: ActionLabelModalProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [customText, setCustomText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  // Pre-populate from existing label on mount
  useEffect(() => {
    if (existingLabel) {
      if (existingLabel.source === 'llm_wrote_own' || existingLabel.source === 'manual') {
        setCustomText(existingLabel.final_text_zh ?? existingLabel.final_text);
        setSelectedIndex(existingLabel.selected_index);
      } else if (existingLabel.source === 'llm') {
        setSelectedIndex(existingLabel.selected_index);
        setCustomText('');
      }
    }
  }, [existingLabel]);

  const displayCandidates = useMemo(() => {
    if (candidates.length > 0) return candidates;
    if (!existingLabel?.candidates_json) return [];
    try {
      const parsed = JSON.parse(existingLabel.candidates_json) as LlmTask[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [candidates, existingLabel?.candidates_json]);

  const hasLlmData = displayCandidates.length > 0;
  const isLoading = llmStatus === 'clipping' || llmStatus === 'loading';
  const hasSelection = selectedIndex !== null && displayCandidates[selectedIndex] != null;
  const hasText = customText.trim().length > 0;
  // Only one of (selection, text) may be active — XOR
  const canSave = !isSaving && !isDeleting && !isTranslating && hasSelection !== hasText;

  async function handleSave() {
    if (!canSave) return;
    const candidatesJson = displayCandidates.length > 0 ? JSON.stringify(displayCandidates) : null;
    const effectiveModel = model ?? existingLabel?.model ?? null;
    const effectiveDuration = llmDurationS ?? existingLabel?.llm_duration_s ?? null;
    let payload: SaveActionLabelPayload;

    if (hasSelection) {
      const selected = displayCandidates[selectedIndex]!;
      payload = {
        final_text: selected.en,
        final_text_zh: selected.zh,
        source: 'llm',
        selected_index: selectedIndex,
        candidates_json: candidatesJson,
        model: effectiveModel,
        llm_duration_s: effectiveDuration,
        updated_by: operator,
      };
    } else {
      const trimmed = customText.trim();
      setIsTranslating(true);
      setTranslateError(null);
      try {
        const revised = await reviseText(episodeId, startSecond, endSecond, trimmed);
        payload = {
          final_text: revised.en,
          final_text_zh: trimmed,
          source: 'manual',
          selected_index: null,
          candidates_json: candidatesJson,
          model: effectiveModel,
          llm_duration_s: effectiveDuration,
          updated_by: operator,
        };
      } catch (err) {
        setTranslateError(String(err));
        return;
      } finally {
        setIsTranslating(false);
      }
    }

    setIsSaving(true);
    try {
      await onSave(startSecond, payload);
      onClose();
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCopy(zh: string, idx: number) {
    await navigator.clipboard.writeText(zh);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  }

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await onDelete(startSecond);
      onClose();
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        className="relative w-full max-w-lg mx-4 rounded-xl border border-brand-border bg-brand-surface shadow-xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-brand-border px-5 py-4">
          <h2 className="text-sm font-semibold text-brand-text-primary">
            Action {startSecond}s &ndash; {endSecond}s
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-brand-text-secondary hover:text-white hover:bg-brand-surface-highlight transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Status area */}
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-brand-text-secondary">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {llmStatus === 'clipping' ? 'Extracting clip...' : 'Analyzing with LLM...'}
            </div>
          )}
          {llmStatus === 'error' && llmError && (
            <div className="rounded border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-400">
              {llmError}
            </div>
          )}

          {/* Candidates */}
          {hasLlmData && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-brand-text-secondary">LLM Suggestions</p>
              <div className="space-y-1.5">
                {displayCandidates.map((candidate, idx) => (
                  <div
                    key={`${candidate.zh}\u0000${candidate.en}`}
                    className={clsx(
                      'relative rounded border px-3 py-2 text-sm transition-colors',
                      selectedIndex === idx
                        ? 'border-brand-orange bg-brand-orange/10'
                        : 'border-brand-border bg-brand-dark hover:border-brand-orange/50'
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={selectedIndex === idx}
                      onClick={() => {
                        const nextIndex = idx === selectedIndex ? null : idx;
                        setSelectedIndex(nextIndex);
                        if (nextIndex !== null) setCustomText('');
                      }}
                      className="w-full text-left pr-7"
                    >
                      <span
                        className={clsx(
                          'block',
                          selectedIndex === idx
                            ? 'text-brand-text-primary'
                            : 'text-brand-text-secondary'
                        )}
                      >
                        {candidate.zh}
                      </span>
                      <span className="block text-[11px] text-brand-text-secondary/60 mt-0.5">
                        {candidate.en}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleCopy(candidate.zh, idx);
                      }}
                      className="absolute right-2 top-2 rounded p-1 text-brand-text-secondary/50 hover:text-brand-text-primary hover:bg-brand-surface-highlight transition-colors"
                      aria-label="Copy Chinese text"
                    >
                      {copiedIdx === idx ? (
                        <Check className="h-3 w-3 text-green-400" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom textarea */}
          <div className="space-y-1.5">
            <label
              className="text-xs font-medium text-brand-text-secondary"
              htmlFor="action-label-custom"
            >
              {hasLlmData ? 'Or write your own:' : 'Action description:'}
            </label>
            <textarea
              id="action-label-custom"
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                if (e.target.value.trim().length > 0) setSelectedIndex(null);
              }}
              rows={3}
              className="w-full rounded border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary placeholder-brand-text-secondary focus:border-brand-orange focus:outline-none resize-none"
              placeholder="Describe the action..."
            />
          </div>

          {/* Translation error */}
          {translateError && (
            <div className="rounded border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-400">
              翻译失败：{translateError}
            </div>
          )}

          {/* Model info */}
          {(model ?? existingLabel?.model) && (
            <p className="text-[10px] text-brand-text-secondary">
              Model: {model ?? existingLabel?.model}
              {(llmDurationS ?? existingLabel?.llm_duration_s) != null && (
                <span>
                  {' '}
                  &middot; {(llmDurationS ?? existingLabel?.llm_duration_s)?.toFixed(1)}s
                </span>
              )}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between border-t border-brand-border px-5 py-3">
          <div className="flex items-center gap-2">
            {existingLabel && (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={isDeleting || isSaving}
                className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs text-red-400 hover:bg-red-950/30 hover:text-red-300 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving || isDeleting || isTranslating}
              className="rounded px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white hover:bg-brand-surface-highlight transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className="flex items-center gap-1.5 rounded bg-brand-orange px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
            >
              {isTranslating ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Translating...
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
